<!--
  NarrativeFeed.vue — Phase 1.3: Unified Activity Feed
  
  Container component with:
    - Agent filter tabs (All / Centinela / Jefe / Supervisor)
    - Severity filter chips
    - Infinite scroll with virtual list hint
    - Renders NarrativeCard.vue for each event
    - "compact" prop for dashboard mini-feed mode
    
  Props:
    compact  — boolean: compact mode (fewer details, no filters)
    limit    — number: max events to show (for dashboard widget)
    agents   — array: pre-filter by agent
    height   — string: container height (default: '100%')

  Events:
    @select  — emitted when user clicks "View" on an event
-->
<template>
  <component
    :is="glass ? 'div' : 'VCard'"
    :class="['narrative-feed', { 'narrative-feed--compact': compact, 'narrative-feed--glass': glass }]"
    v-bind="glass ? {} : { rounded: 'lg' }"
  >
    <!-- Search + Filter bar (full mode, non-glass) -->
    <div v-if="!compact && !glass" class="nf-search-bar d-flex align-center gap-2 px-3 pt-3 pb-2">
      <!-- Filter button -->
      <VMenu
        v-model="filterMenuOpen"
        :close-on-content-click="false"
        location="bottom start"
        offset="4"
      >
        <template #activator="{ props: menuProps }">
          <VBtn
            v-bind="menuProps"
            icon
            size="small"
            variant="tonal"
            :color="hasActiveFilters ? 'primary' : 'default'"
          >
            <VIcon size="18" icon="mdi-filter-variant" />
            <VBadge
              v-if="hasActiveFilters"
              dot
              color="primary"
              offset-x="-2"
              offset-y="-2"
            />
          </VBtn>
        </template>

        <VCard min-width="220" class="nf-filter-menu">
          <VCardText class="pa-3">
            <!-- Agent category section -->
            <div class="text-overline text-disabled mb-1">{{ t('dashboard.feed.category') }}</div>
            <VCheckbox
              v-for="agent in agentOptions"
              :key="agent.value"
              v-model="selectedAgents"
              :label="agent.label"
              :value="agent.value"
              :color="agent.color"
              density="compact"
              hide-details
              class="nf-filter-checkbox"
            >
              <template #label>
                <div class="d-flex align-center gap-2">
                  <VIcon :icon="agent.icon" size="14" :color="agent.color" />
                  <span class="text-body-2">{{ agent.label }}</span>
                </div>
              </template>
            </VCheckbox>

            <VDivider class="my-2" />

            <!-- Severity section -->
            <div class="text-overline text-disabled mb-1">{{ t('dashboard.feed.alertLevel') }}</div>
            <VCheckbox
              v-for="sev in severityOptions"
              :key="sev.value"
              v-model="selectedSeverityValues"
              :label="sev.label"
              :value="sev.value"
              :color="sev.color"
              density="compact"
              hide-details
              class="nf-filter-checkbox"
            >
              <template #label>
                <div class="d-flex align-center gap-2">
                  <span
                    class="nf-sev-dot"
                    :style="{ background: `rgb(var(--v-theme-${sev.color === 'default' ? 'secondary' : sev.color}))` }"
                  />
                  <span class="text-body-2">{{ sev.label }}</span>
                </div>
              </template>
            </VCheckbox>

            <VDivider class="my-2" />

            <div class="d-flex justify-space-between">
              <VBtn
                variant="text"
                size="small"
                color="secondary"
                @click="resetFilters"
              >
                {{ t('dashboard.feed.reset') }}
              </VBtn>
              <VBtn
                variant="text"
                size="small"
                @click="markAllRead"
                :title="t('dashboard.feed.markAllRead')"
              >
                <VIcon start size="14" icon="mdi-check-all" />
                {{ t('dashboard.feed.markRead') }}
              </VBtn>
            </div>
          </VCardText>
        </VCard>
      </VMenu>

      <!-- Search field -->
      <VTextField
        v-model="eventSearch"
        :placeholder="t('dashboard.feed.searchEvents')"
        prepend-inner-icon="mdi-magnify"
        variant="outlined"
        density="compact"
        hide-details
        clearable
        class="flex-grow-1"
      />

      <!-- Unread badge -->
      <VChip
        v-if="stats.unread > 0"
        color="error"
        size="x-small"
        variant="flat"
      >
        {{ stats.unread }}
      </VChip>
    </div>

    <!-- Active filter badges (full mode) -->
    <div
      v-if="!compact && !glass && hasActiveFilters"
      class="nf-active-filters d-flex flex-wrap gap-1 px-3 pb-2"
    >
      <VChip
        v-for="agent in activeAgentChips"
        :key="'a-' + agent.value"
        size="x-small"
        :color="agent.color"
        variant="tonal"
        closable
        @click:close="removeAgentFilter(agent.value)"
      >
        {{ agent.label }}
      </VChip>
      <VChip
        v-for="sev in activeSeverityChips"
        :key="'s-' + sev.value"
        size="x-small"
        :color="sev.color"
        variant="tonal"
        closable
        @click:close="removeSeverityFilter(sev.value)"
      >
        {{ sev.label }}
      </VChip>
    </div>

    <!-- Compact header -->
    <VCardItem v-if="!glass && (compact || !hasActiveFilters)">
      <VCardTitle class="text-body-1 font-weight-semibold d-flex align-center gap-2">
        <VChip
          v-if="stats.unread > 0"
          color="error"
          size="x-small"
          variant="flat"
        >
          {{ stats.unread }}
        </VChip>
      </VCardTitle>
    </VCardItem>

    <!-- Event list -->
    <VCardText
      class="narrative-feed-scroll"
      :class="glass ? 'pa-1' : 'pa-3'"
      :style="{ maxHeight: height, overflowY: 'auto' }"
    >
      <!-- Loading initial -->
      <div v-if="loading" class="d-flex flex-column gap-3">
        <VSkeletonLoader v-for="i in 3" :key="i" type="card" />
      </div>

      <!-- Empty state -->
      <div
        v-else-if="displayEvents.length === 0"
        class="text-center py-8 text-disabled"
      >
        <VIcon size="48" icon="mdi-bell-sleep-outline" class="mb-2" />
        <div class="text-body-2">{{ t('dashboard.feed.noEventsYet') }}</div>
        <div class="text-caption">{{ t('dashboard.feed.eventsRealtime') }}</div>
      </div>

      <!-- Events -->
      <div v-else class="d-flex flex-column gap-2">
        <NarrativeCard
          v-for="evt in displayEvents"
          :key="evt.id"
          :agent="evt.agent"
          :title="evt.title"
          :narrative="evt.narrative"
          :timestamp="formatTime(evt.timestamp)"
          :severity="evt.severity"
          :actions="compact ? [] : buildActions(evt)"
          :glass="glass"
          :unread="!evt.read"
          :class="{ 'narrative-card--unread': !glass && !evt.read }"
          @click="handleSelect(evt)"
        />

        <!-- Load more trigger -->
        <div
          v-if="hasMore && !compact"
          v-intersect="onIntersect"
          class="text-center py-4"
        >
          <VProgressCircular
            v-if="loadingMore"
            indeterminate
            size="24"
            width="2"
            color="primary"
          />
          <VBtn
            v-else
            variant="text"
            size="small"
            color="primary"
            @click="loadMore"
          >
            {{ t('dashboard.feed.loadOlder') }}
          </VBtn>
        </div>
      </div>
    </VCardText>
  </component>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useNarrativeFeed } from '@/@core/composable/useNarrativeFeed'
import NarrativeCard from './NarrativeCard.vue'

const { t } = useI18n()

const props = defineProps({
  compact: { type: Boolean, default: false },
  limit: { type: Number, default: 0 },
  agents: { type: Array, default: null },
  height: { type: String, default: '100%' },
  showSeverityFilter: { type: Boolean, default: true },
  /** Pass-through to NarrativeCard: renders compact dark C5 rows */
  glass: { type: Boolean, default: false },
})

const emit = defineEmits(['select'])

/* ─── Narrative Feed composable ───────────────────────────────── */
const {
  filteredEvents,
  stats,
  loading,
  loadingMore,
  hasMore,
  activeAgentFilter,
  activeSeverityFilter,
  loadMore,
  markRead,
  markAllRead,
} = useNarrativeFeed({
  agents: props.agents,
})

/* ─── Search + Filter state ───────────────────────────────────── */
const eventSearch = ref('')
const filterMenuOpen = ref(false)

/* ─── Agent filter ────────────────────────────────────────────── */
const agentOptions = computed(() => [
  { value: 'centinela',  label: t('dashboard.agents.centinela'),  color: 'agent-centinela',  icon: 'mdi-shield-lock-outline' },
  { value: 'jefe',       label: t('dashboard.agents.jefe'),        color: 'agent-jefe',       icon: 'mdi-truck-fast-outline' },
  { value: 'supervisor', label: t('dashboard.agents.supervisor'),  color: 'agent-supervisor', icon: 'mdi-hard-hat' },
])

const allAgentValues = ['centinela', 'jefe', 'supervisor']
const selectedAgents = ref([...allAgentValues])

watch(selectedAgents, (vals) => {
  activeAgentFilter.value = vals.length ? vals : [...allAgentValues]
}, { deep: true })

/* ─── Severity filter ─────────────────────────────────────────── */
const severityOptions = computed(() => [
  { value: 'critical', label: t('dashboard.severity.critical'), color: 'error' },
  { value: 'high',     label: t('dashboard.severity.high'),     color: 'warning' },
  { value: 'medium',   label: t('dashboard.severity.medium'),   color: 'info' },
  { value: 'low',      label: t('dashboard.severity.low'),      color: 'secondary' },
  { value: 'info',     label: t('dashboard.severity.info'),     color: 'default' },
])

const allSeverityValues = ['critical', 'high', 'medium', 'low', 'info']
const selectedSeverityValues = ref([...allSeverityValues])

watch(selectedSeverityValues, (vals) => {
  activeSeverityFilter.value = vals.length ? vals : [...allSeverityValues]
}, { deep: true })

/* ─── Active filter chips (for display below search bar) ──────── */
const hasActiveFilters = computed(() =>
  selectedAgents.value.length < allAgentValues.length ||
  selectedSeverityValues.value.length < allSeverityValues.length,
)

const activeAgentChips = computed(() =>
  selectedAgents.value.length < allAgentValues.length
    ? agentOptions.value.filter(a => selectedAgents.value.includes(a.value))
    : [],
)

const activeSeverityChips = computed(() =>
  selectedSeverityValues.value.length < allSeverityValues.length
    ? severityOptions.value.filter(s => selectedSeverityValues.value.includes(s.value))
    : [],
)

function removeAgentFilter(val) {
  selectedAgents.value = selectedAgents.value.filter(v => v !== val)
}

function removeSeverityFilter(val) {
  selectedSeverityValues.value = selectedSeverityValues.value.filter(v => v !== val)
}

function resetFilters() {
  selectedAgents.value = [...allAgentValues]
  selectedSeverityValues.value = [...allSeverityValues]
}

/* ─── Display events (with optional limit + text search) ──────── */
const displayEvents = computed(() => {
  let evts = filteredEvents.value

  // Text search filter
  if (eventSearch.value) {
    const q = eventSearch.value.toLowerCase()
    evts = evts.filter(e =>
      (e.title || '').toLowerCase().includes(q) ||
      (e.narrative || '').toLowerCase().includes(q) ||
      (e.agent || '').toLowerCase().includes(q),
    )
  }

  if (props.limit > 0) return evts.slice(0, props.limit)
  return evts
})

/* ─── Time formatting ─────────────────────────────────────────── */
function formatTime(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  const now = new Date()
  const diffMs = now - d
  const diffMin = Math.floor(diffMs / 60_000)

  if (diffMin < 1) return t('dashboard.feed.justNow')
  if (diffMin < 60) return t('dashboard.feed.minutesAgo', { count: diffMin })

  const diffHours = Math.floor(diffMin / 60)
  if (diffHours < 24) return t('dashboard.feed.hoursAgo', { count: diffHours })

  // Same week: show day + time
  return d.toLocaleDateString(undefined, {
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  })
}

/* ─── Event actions ───────────────────────────────────────────── */
function buildActions(evt) {
  const actions = [
    {
      label: t('dashboard.feed.view'),
      icon: 'mdi-eye-outline',
      handler: () => handleSelect(evt),
    },
  ]
  if (evt.severity === 'critical' || evt.severity === 'high') {
    actions.push({
      label: t('dashboard.feed.action'),
      icon: 'mdi-lightning-bolt',
      handler: () => emit('select', { ...evt, action: true }),
    })
  }
  return actions
}

function handleSelect(evt) {
  markRead(evt.id)
  emit('select', evt)
}

/* ─── Infinite scroll intersection observer ───────────────────── */
function onIntersect(isIntersecting) {
  if (isIntersecting && !loadingMore.value && hasMore.value) {
    loadMore()
  }
}
</script>

<style lang="scss" scoped>
.narrative-feed {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.narrative-feed--glass {
  background: transparent !important;
  box-shadow: none !important;
}

.narrative-feed--compact {
  .narrative-feed-scroll {
    max-height: 400px;
  }
}

.narrative-card--unread {
  border-inline-start: 3px solid rgb(var(--v-theme-primary));
}

.narrative-feed-scroll {
  flex: 1;
  min-height: 0;

  // Custom scrollbar
  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(var(--v-theme-on-surface), 0.15);
    border-radius: 2px;
  }
}

/* ─── Filter menu ────────────────────────────────────────────── */
.nf-filter-menu {
  .nf-filter-checkbox {
    margin-block: 0;

    :deep(.v-selection-control) {
      min-height: 28px;
    }
  }
}

.nf-sev-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.nf-active-filters {
  flex-shrink: 0;
}

.nf-search-bar {
  flex-shrink: 0;
}
</style>
